// ============================================================
// DecoupeConfirmation — ce que la découpe va faire, AVANT de la faire.
// ------------------------------------------------------------
// L'invariant de la découpe : jamais de perte silencieuse. Cet écran dit
// combien de pièces naissent, si le cartouche est joint, ce que devient
// la pièce de la feuille entière — et, si un humain y a travaillé, CE
// QUI SERA PERDU, mot pour mot. Le serveur refuse de toute façon sans
// cette confirmation (409) : l'écran ne fait que la demander d'abord.
//
// La consigne n'est PAS recopiée par défaut (décision de Thibaud, 14/10) :
// sur 2026-10-013 elle disait « 2 pièces assemblées sur le plan » — la
// recopier sur chacune aurait été faux. On l'affiche, on laisse choisir.
// ============================================================

import { useState } from 'react';
import Button from '../../ui/Button.jsx';
import api from '../../lib/api.js';
import { resumeDecoupe } from './decoupeZones.js';

export function DecoupeConfirmation({ devisId, prep, zones, mode, onRetour, onTermine }) {
  const [recopier, setRecopier] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const annulation = mode === 'annulation';
  const pertes = prep.pieces.map((p) => p.perte).filter(Boolean);
  const retours = prep.pieces.filter((p) => p.retours_atelier > 0);
  const consigne = prep.pieces.find((p) => p.consigne)?.consigne ?? null;
  const url = `/devis/${devisId}/plans/${prep.page.id}/decoupe`;

  async function valider() {
    setEnvoi(true);
    setErreur(null);
    try {
      if (annulation) {
        await api.delete(url, { body: { confirmer_perte: pertes.length > 0 } });
      } else {
        await api.post(url, {
          zones: zones.map(({ type, libelle, x, y, w, h }) => ({ type, libelle, x, y, w, h })),
          recopier_consigne: recopier,
          confirmer_perte: pertes.length > 0,
        });
      }
      onTermine();
    } catch (err) {
      setErreur(err.message);
      setEnvoi(false);
    }
  }

  const lignes = annulation
    ? [
      `Les ${prep.pieces.length} pièce(s) découpée(s) seront supprimées.`,
      'La page entière redevient une pièce, refaite à partir de sa lecture d’origine — sans nouvel appel payant.',
    ]
    : resumeDecoupe(zones, prep.pieces);

  return (
    <div className="decoupe-confirmation">
      <ul className="decoupe-confirmation__resume">
        {lignes.map((l) => <li key={l}>{l}</li>)}
      </ul>

      {retours.length ? (
        <p className="field__error" role="alert">
          {retours.map((p) => `« ${p.nom ?? p.id} »`).join(', ')} porte un retour d’atelier : ses temps
          réels ne se perdent pas, la découpe est impossible.
        </p>
      ) : null}

      {pertes.length ? (
        <div className="decoupe-confirmation__perte" role="alert">
          {pertes.map((p) => <p key={p}><strong>{p}</strong></p>)}
        </div>
      ) : null}

      {!annulation && consigne ? (
        <label className="row decoupe-confirmation__consigne">
          <input type="checkbox" aria-label="Recopier la consigne sur chaque nouvelle pièce" checked={recopier} onChange={(e) => setRecopier(e.target.checked)} />
          <span>
            Recopier la consigne sur chaque nouvelle pièce :
            {' '}<q>{consigne}</q>
          </span>
        </label>
      ) : null}

      {!annulation ? (
        <p className="field__hint">
          Chaque pièce part avec la quantité de la feuille ({prep.pieces[0]?.qty ?? 'quantité du mail, sinon 1'}),
          marquée « à vérifier » : rien ne dit qu’il en faut autant de chacune.
        </p>
      ) : null}

      {erreur ? <p className="field__error" role="alert">{erreur}</p> : null}

      <div className="decoupe__actions">
        <Button variant="ghost" disabled={envoi} onClick={onRetour}>← Retour au tracé</Button>
        <Button variant={annulation ? 'danger' : 'primary'} disabled={envoi || retours.length > 0}
          onClick={valider}>
          {annulation ? 'Annuler la découpe' : pertes.length ? 'Découper, et perdre ce travail' : 'Découper'}
        </Button>
      </div>
    </div>
  );
}

export default DecoupeConfirmation;
